import { useState } from "react";
import { Modal, Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { CredentialsStep } from "views/settings-integration/components/AddIntegrationModal/CredentialsStep";
import { FirstStep } from "views/settings-integration/components/AddIntegrationModal/FirstStep";
import { Header } from "views/settings-integration/components/AddIntegrationModal/Header";
import { NameStep } from "views/settings-integration/components/AddIntegrationModal/NameStep";
import { SecondStep } from "views/settings-integration/components/AddIntegrationModal/SecondStep";
import {
  IntegrationId,
  DIRECT_CONNECT_INTEGRATIONS,
  isPmsIntegration,
  OTHERS_WITH_CREDENTIALS,
  Step
} from "views/settings-integration/components/AddIntegrationModal/types";
import { PropertyManagementSystem } from "shared/types/partner";

interface AddIntegrationModalProps {
  opened: boolean;
  onClose: () => void;
}

const needsCredentialStep = (
  id: PropertyManagementSystem | IntegrationId
): boolean => isPmsIntegration(id) || OTHERS_WITH_CREDENTIALS.has(id);

const getTotalSteps = (
  id: PropertyManagementSystem | IntegrationId
): number => {
  if (DIRECT_CONNECT_INTEGRATIONS.has(id)) return 1;
  if (needsCredentialStep(id)) return 3;
  return 2;
};

export const AddIntegrationModal = ({
  opened,
  onClose
}: AddIntegrationModalProps) => {
  const { type } = useAuthContext();
  const [selectedIntegration, setSelectedIntegration] = useState<
    PropertyManagementSystem | IntegrationId
  >("" as PropertyManagementSystem | IntegrationId);
  const [currentStep, setCurrentStep] = useState<Step>(Step.FIRST);
  const [credentials, setCredentials] = useState<
    Record<string, string | boolean>
  >({});

  const handleClose = () => {
    onClose();
    setSelectedIntegration("" as PropertyManagementSystem | IntegrationId);
    setCurrentStep(Step.FIRST);
    setCredentials({});
  };

  const totalSteps = getTotalSteps(selectedIntegration);

  const handleCredentialsSubmit = (creds: Record<string, string | boolean>) => {
    setCredentials(creds);
    setCurrentStep(Step.THIRD);
  };

  if (type.isMultiPMS)
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        panelClassName="max-w-[480px] w-full flex flex-col p-6 shadow-xl max-h-[90vh]"
      >
        <Header currentStep={currentStep} totalSteps={totalSteps} />

        <Show if={currentStep === Step.FIRST}>
          <FirstStep
            selectedIntegration={selectedIntegration}
            setSelectedIntegration={setSelectedIntegration}
            setCurrentStep={setCurrentStep}
            closeModal={handleClose}
          />
        </Show>

        <Show
          if={
            currentStep === Step.SECOND &&
            needsCredentialStep(selectedIntegration)
          }
        >
          <CredentialsStep
            selectedIntegration={selectedIntegration}
            onCredentialsSubmit={handleCredentialsSubmit}
            setCurrentStep={setCurrentStep}
            credentials={credentials}
          />
        </Show>

        <Show
          if={
            currentStep === Step.THIRD &&
            needsCredentialStep(selectedIntegration)
          }
        >
          <NameStep
            selectedIntegration={selectedIntegration}
            credentials={credentials}
            setCurrentStep={setCurrentStep}
            closeModal={handleClose}
          />
        </Show>

        {/* Non-PMS integrations without name step (GA, HubSpot) go to SecondStep */}
        <Show
          if={
            currentStep === Step.SECOND &&
            !needsCredentialStep(selectedIntegration)
          }
        >
          <SecondStep
            setCurrentStep={setCurrentStep}
            closeModal={handleClose}
            selectedIntegration={selectedIntegration}
          />
        </Show>
      </Modal>
    );

  // Legacy flow for non-multi-PMS partners
  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      panelClassName="max-w-[480px] w-full flex flex-col p-6 shadow-xl max-h-[90vh]"
    >
      <Header currentStep={currentStep} totalSteps={2} />

      <Show if={currentStep === Step.FIRST}>
        <FirstStep
          selectedIntegration={selectedIntegration}
          setSelectedIntegration={setSelectedIntegration}
          setCurrentStep={setCurrentStep}
          closeModal={handleClose}
        />
      </Show>

      <Show if={currentStep === Step.SECOND}>
        <SecondStep
          setCurrentStep={setCurrentStep}
          closeModal={handleClose}
          selectedIntegration={selectedIntegration}
        />
      </Show>
    </Modal>
  );
};
